import { useCallback, useState } from 'react';
import type { RuntimeCapabilities } from '../../../shared/api/runtime/capabilities';
import type { ReconnectionResultState } from '../lib/validationReconnectNormalizer';
import type { ValidationRunProgress } from '../lib/validationRunProgress';
import type { ValidationRuntimeSettings } from '../lib/validationRuntimeSettings';
import type { ValidationExportScope } from '../lib/validationReportNormalizer';
import type { PcfValidationReport, ValidationCheckResult, ValidationDefinition } from '../types';
import { useVerifiedSetupRevision } from '../../../shared/stores/useUIStore';
import { getGlobalValidationRunCoordinator } from '../lib/validationRunCoordinatorSingleton';
import { useValidationBootstrap } from './useValidationBootstrap';
import { useValidationExport } from './useValidationExport';
import { useValidationHighlight } from './useValidationHighlight';
import { useValidationReconnect } from './useValidationReconnect';
import { useValidationReportCurrency } from './useValidationReportCurrency';
import { useValidationRunner } from './useValidationRunner';
import { useValidationSettings } from './useValidationSettings';

export interface RunValidationOptions {
  forceFresh?: boolean;
  mergeIntoCurrentReport?: boolean;
  silentCacheHit?: boolean;
}

export interface ValidationStateReturn {
  runtimeCapabilities: RuntimeCapabilities;
  // Connection
  isReady: boolean;
  hasCheckedWebView: boolean;
  isWebView2: boolean;

  // Data
  report: PcfValidationReport | null;
  validations: ValidationDefinition[];
  selectedValidations: number[];
  documentInfo: any;
  documentInfoChecked: boolean;
  validationSettings: ValidationRuntimeSettings;

  // Report currency
  /** True only while the exposed report exactly describes the live setup. */
  isReportCurrent: boolean;
  /** True while a stored report exists but cannot be presented as current. */
  hasStaleReport: boolean;

  // UI state
  loading: boolean;
  runProgress: ValidationRunProgress | null;
  showConfigDialog: boolean;
  reconnecting: boolean;
  validationSettingsSaving: boolean;
  reconnectionResult: ReconnectionResultState | null;
  reconnectDialogOpen: boolean;

  // Setters
  setShowConfigDialog: (open: boolean) => void;

  // Handlers
  handleRunValidation: (
    enabledValidationIds?: number[],
    options?: RunValidationOptions
  ) => Promise<void>;
  handleHighlightElements: (elementIds: string[]) => Promise<void>;
  handleExportJson: (
    scope?: ValidationExportScope,
    validationId?: number,
    selectedCheck?: ValidationCheckResult | null
  ) => Promise<void>;
  handleExportHtml: (
    scope?: ValidationExportScope,
    validationId?: number,
    selectedCheck?: ValidationCheckResult | null
  ) => Promise<void>;
  handleReconnectPair: (elementId1: string, elementId2: string) => Promise<void>;
  handleReconnectAll: (validationId: number) => Promise<void>;
  handleUpdateReconnectGapMargin: (marginMillimeters: number) => Promise<void>;
  closeReconnectDialog: () => void;

  // Config dialog helpers
  handleToggleValidation: (id: number) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
}

export function useValidationState(): ValidationStateReturn {
  // Process-wide singleton: a per-mount coordinator would drop the run cache on
  // every page navigation, forcing a full re-validation after each revisit.
  const [coordinator] = useState(
    () => getGlobalValidationRunCoordinator()
  );
  // The setup revision every report is judged against: evidence captured under
  // an older revision can no longer describe the setup the user is looking at.
  const verifiedRevision = useVerifiedSetupRevision();
  const settings = useValidationSettings({ coordinator });
  const bootstrap = useValidationBootstrap({
    setValidationSettings: settings.setValidationSettings,
    coordinator,
    verifiedRevision,
    reconnectGapMarginMillimeters: settings.validationSettings.reconnectGapMarginMillimeters,
  });

  const runner = useValidationRunner({
    validations: bootstrap.validations,
    selectedValidations: bootstrap.selectedValidations,
    validationSettings: settings.validationSettings,
    documentInfo: bootstrap.documentInfo,
    report: bootstrap.report,
    commitReport: bootstrap.commitReport,
    setDocumentInfo: bootstrap.setDocumentInfo,
    coordinator,
    verifiedRevision,
  });

  const isReportCurrent = useValidationReportCurrency({
    report: bootstrap.report,
    documentInfo: bootstrap.documentInfo,
    validations: bootstrap.validations,
    selectedValidations: bootstrap.selectedValidations,
    reconnectGapMarginMillimeters: settings.validationSettings.reconnectGapMarginMillimeters,
    verifiedRevision,
  });

  const exportActions = useValidationExport({
    report: bootstrap.report,
    documentInfo: bootstrap.documentInfo,
    isReportCurrent,
  });
  const highlight = useValidationHighlight();
  const { handleRunValidation } = runner;
  const runFocusedValidation = useCallback(
    async (validationIds: number[]) => {
      await handleRunValidation(validationIds, {
        forceFresh: true,
        mergeIntoCurrentReport: true,
      });
    },
    [handleRunValidation]
  );
  const reconnect = useValidationReconnect({
    runtimeCapabilities: bootstrap.runtimeCapabilities,
    coordinator,
    runFocusedValidation,
  });

  const [showConfigDialog, setShowConfigDialog] = useState(false);

  // A report that cannot prove it describes the setup on screen is not current:
  // consumers only ever see it through the currency gate, so a stale record can
  // be rendered as an explicit stale state but never presented as the result.
  const exposedReport = isReportCurrent ? bootstrap.report : null;
  const hasStaleReport = Boolean(bootstrap.report) && !isReportCurrent;

  return {
    runtimeCapabilities: bootstrap.runtimeCapabilities,
    isReady: bootstrap.isReady,
    hasCheckedWebView: bootstrap.hasCheckedWebView,
    isWebView2: bootstrap.isWebView2,

    report: exposedReport,
    validations: bootstrap.validations,
    selectedValidations: bootstrap.selectedValidations,
    documentInfo: bootstrap.documentInfo,
    documentInfoChecked: bootstrap.documentInfoChecked,
    validationSettings: settings.validationSettings,

    isReportCurrent,
    hasStaleReport,

    loading: runner.loading,
    runProgress: runner.runProgress,
    showConfigDialog,
    reconnecting: reconnect.reconnecting,
    validationSettingsSaving: settings.validationSettingsSaving,
    reconnectionResult: reconnect.reconnectionResult,
    reconnectDialogOpen: reconnect.reconnectDialogOpen,

    setShowConfigDialog,

    handleRunValidation: runner.handleRunValidation,
    handleHighlightElements: highlight.handleHighlightElements,
    handleExportJson: exportActions.handleExportJson,
    handleExportHtml: exportActions.handleExportHtml,
    handleReconnectPair: reconnect.handleReconnectPair,
    handleReconnectAll: reconnect.handleReconnectAll,
    handleUpdateReconnectGapMargin: settings.handleUpdateReconnectGapMargin,
    closeReconnectDialog: reconnect.closeReconnectDialog,

    handleToggleValidation: bootstrap.handleToggleValidation,
    handleSelectAll: bootstrap.handleSelectAll,
    handleDeselectAll: bootstrap.handleDeselectAll,
  };
}
